import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { globalActions } from 'app/containers/Global/slice';
import i18next from 'i18next';
import { put, select, takeLatest } from 'redux-saga/effects';
import { userActions as actions } from '.';
import userApi from '../userApi';
import {
  getPermissionNumbersFromBooleans,
  getPermissionsBooleansFromNumbers,
} from './helpers';
import {
  IGetUserDetailsPayload,
  IGetUserListPayload,
  ISubmitAddUserFormPayload,
  ISubmitAddUserToCompanyPayload,
  ISubmitRemoveUserFromEnvPayload,
  IUserCompaniesListItem,
  IUserCompaniesListResponseItem,
  IUserDetails,
  IUserListItem,
  IUserListResponseItem,
} from './types';

function* getUserListSaga(action: PayloadAction<IGetUserListPayload>) {
  try {
    const { envId, companyId } = action.payload;
    let userResponseList: IUserListResponseItem[] = [];

    if (companyId !== null) {
      userResponseList = yield userApi.getUsersByCompany(envId, companyId);
    } else {
      userResponseList = yield userApi.getAllUsers(envId);
    }

    const users = userResponseList.map(user => {
      const { userHiikoEnvironmentPermissions: p, ...userData } = user;
      const mappedUser: IUserListItem = {
        ...userData,
        permissions: getPermissionNumbersFromBooleans(p),
      };
      return mappedUser;
    });

    yield put(actions.setUserList(users));
  } catch (err) {
    yield put(actions.setUserList([]));
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setUserListLoading(false));
  }
}

function* getUserDetailsSaga(action: PayloadAction<IGetUserDetailsPayload>) {
  try {
    yield put(actions.setUserDetailsLoading(true));

    const { userId, envId } = action.payload;

    const user: IUserDetails = yield userApi.getUserById(userId);
    yield put(actions.setUserDetails(user));

    const userCompaniesResponse: IUserCompaniesListResponseItem[] = yield userApi.getUserCompanies(
      userId,
      envId,
    );
    const userCompanies: IUserCompaniesListItem[] = userCompaniesResponse.map(
      userCompany => {
        const { userCompanyPermissions, ...rest } = userCompany;
        const listItem: IUserCompaniesListItem = {
          ...rest,
          id: userCompanyPermissions.company.id,
          userCompanyPermissions: getPermissionNumbersFromBooleans(
            userCompanyPermissions,
          ),
        };
        return listItem;
      },
    );
    yield put(actions.setUserDetailsCompanies(userCompanies));
  } catch (err) {
    yield put(actions.setUserDetailsCompanies([]));

    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setUserDetailsLoading(false));
  }
}

function* submitAddUserFormSaga(
  action: PayloadAction<ISubmitAddUserFormPayload>,
) {
  try {
    yield put(actions.setAddUserFormLoading(true));

    const { email, role, envId, permissions, type } = action.payload;

    const data = {
      hiikoEnvironmentAdministrationView: getPermissionsBooleansFromNumbers(
        permissions,
      ),
      email,
      role,
    };

    yield userApi.createOrUpdateUserHiikoEnvironment({ envId, data });

    const companyId = yield select(selectSelectedCompanyId);
    yield put(actions.getUserList({ companyId, envId }));

    yield put(actions.toggleAddUserForm({ isOpen: false }));
    if (type === 'add') {
      notification.success({
        message: i18next?.t(
          'User.Notification.UserInvited',
          'User has been successfully invited.',
        ),
        description: i18next?.t('User.Notification.UserInvitedDescription', {
          defaultValue:
            '"{{email}}" will receive an invitation with a link to Hikos registration.',
          email,
        }),
        placement: 'bottomRight',
      });
    } else {
      notification.success({
        message: i18next?.t(
          'User.Notification.UserPermissionsUpdated',
          'User permissions been successfully updated.',
        ),

        placement: 'bottomRight',
      });
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setAddUserFormLoading(false));
  }
}

function* submitAddUserToCompanySaga(
  action: PayloadAction<ISubmitAddUserToCompanyPayload>,
) {
  try {
    yield put(actions.setAddUserCompanyFormLoading(true));

    const {
      email,
      role,
      envId,
      companyId,
      userId,
      permissions,
      type,
    } = action.payload;
    const data = {
      administrationView: getPermissionsBooleansFromNumbers(permissions),
      email,
      role,
    };

    yield userApi.createOrUpdateUserCompany({ envId, companyId, data });

    yield put(actions.getUserDetails({ userId, envId }));

    yield put(actions.toggleAddUserCompanyForm({ isOpen: false }));
    if (type === 'add') {
      notification.success({
        message: i18next?.t(
          'User.Notification.UserAddedToCompany',
          'User has been successfully added to company.',
        ),

        placement: 'bottomRight',
      });
    } else {
      notification.success({
        message: i18next?.t(
          'User.Notification.UserCompanyPermissionsUpdated',
          'User permissions in company has been successfully updated.',
        ),

        placement: 'bottomRight',
      });
    }
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setAddUserCompanyFormLoading(false));
  }
}

function* submitRemoveUserFromEnvSaga(
  action: PayloadAction<ISubmitRemoveUserFromEnvPayload>,
) {
  try {
    const { envId, userId } = action.payload;

    yield userApi.removeUserFromEnvironment({ envId, userId });
    const companyId = yield select(selectSelectedCompanyId);
    yield put(actions.getUserList({ companyId, envId }));

    notification.success({
      message: i18next?.t(
        'User.Notification.UserDeletedFromEnv',
        'User has been successfully removed from the environment.',
      ),

      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
  }
}

export function* userSaga() {
  yield takeLatest(
    actions.submitRemoveUserFromEnv.type,
    submitRemoveUserFromEnvSaga,
  );

  yield takeLatest(actions.getUserList.type, getUserListSaga);

  yield takeLatest(actions.getUserDetails.type, getUserDetailsSaga);

  yield takeLatest(actions.submitAddUserForm.type, submitAddUserFormSaga);

  yield takeLatest(
    actions.submitAddUserToCompany.type,
    submitAddUserToCompanySaga,
  );
}
